import axios from 'axios'

const NOMINATIM_BASE_URL = 'https://nominatim.openstreetmap.org'

const nominatimClient = axios.create({
  baseURL: NOMINATIM_BASE_URL
})

const cache = new Map()
const CACHE_TTL_MS = 15 * 60 * 1000

function getCached(key) {
  const entry = cache.get(key)
  if (!entry) return undefined
  if (Date.now() - entry.ts > CACHE_TTL_MS) {
    cache.delete(key)
    return undefined
  }
  return entry.data
}

function setCache(key, data) {
  cache.set(key, { data, ts: Date.now() })
}

/**
 * Search for places by query string.
 * @param {string} query - Search query
 * @param {object} options - Optional params: limit (default 10), polygonThreshold (outline simplification, degrees)
 * @returns {Promise<Array>} Array of search results, empty array on error
 */
export async function searchPlaces(query, { limit = 10, polygonThreshold, signal } = {}) {
  const cacheKey = `search:${query}:${limit}:${polygonThreshold}`
  const cached = getCached(cacheKey)
  if (cached) return cached

  try {
    const response = await nominatimClient.get('/search', {
      params: {
        q: query,
        polygon_geojson: 1,
        polygon_threshold: polygonThreshold,
        format: 'json',
        limit
      },
      signal
    })
    setCache(cacheKey, response.data)
    return response.data
  } catch (error) {
    console.error('Nominatim search failed:', error.message || error)
    return []
  }
}

/**
 * Get detailed place information by OSM object. Not by place_id: place_ids differ
 * between the replicas behind nominatim.openstreetmap.org, so a search result's
 * place_id can resolve to an unrelated place on the next request.
 * @param {string} osmType - 'node' | 'way' | 'relation' (or N/W/R)
 * @param {number} osmId - The OSM object id
 * @returns {Promise<object|null>} Place details object, or null on error
 */
export async function getPlaceDetails(osmType, osmId) {
  const osmtype = osmType[0].toUpperCase()
  const cacheKey = `details:${osmtype}${osmId}`
  const cached = getCached(cacheKey)
  if (cached) return cached

  try {
    const response = await nominatimClient.get('/details.php', {
      params: {
        osmtype,
        osmid: osmId,
        addressdetails: 1,
        hierarchy: 0,
        group_hierarchy: 1,
        format: 'json'
      }
    })
    setCache(cacheKey, response.data)
    return response.data
  } catch (error) {
    console.error('Nominatim place details failed:', error.message || error)
    return null
  }
}
